import React, { memo, useContext } from "react";
import { Empty } from "antd";
import moment from "moment";
import Permission from "app/components/Permission/Permission";
import { PermissionEnum } from "app/constants/permissionConst";
import PageFilter from "app/components/PageFilter/PageFilter";
import { useTranslation } from "react-i18next";
import { UserContext } from "app/store/contexts/userContext";
import { DateFormats } from "app/constants/dateConst";
import ChartTestResults from "./components/ChartTestResults/ChartTestResults";
import ChartSubstances from "./components/ChartSubstances/ChartSubstances";
import ChartNumberOfTests from "./components/ChartNumberOfTests/ChartNumberOfTests";
import ChartFalsePositive from "./components/ChartFalsePositive/ChartFalsePositive";
import { TableUsers } from "./components/TableUsers/TableUsers";
import KeyStatistics from "./components/KeyStatistics/KeyStatistics";
import DashboardMap from "./components/DashboardMap/DashboardMap";
import styles from "./DashboardPremium.module.scss";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";

const DashboardPremium = () => {
  const { t } = useTranslation();

  const { userInfo } = useContext(UserContext);
  const {
    customerId,
    siteId,
    period,
    dates,
    routes: filterRoutes,
  } = userInfo.filters;
  const { type: userType } = userInfo;

  // Formatted dates used for API calls
  const filterDates = {
    start: !!dates ? dates[0]?.format(DateFormats.DATE) : undefined,
    end: !!dates ? dates[1]?.format(DateFormats.DATE) : undefined,
  };

  /**
   * Formatted period string. Is usually displayed in DashboardItemLayout
   * as a sub title.
   */
  const periodRange =
    (!!period ? t(`pageFilter.${period}`) + " (" : "") +
    `${moment(filterDates.start).format(
      DateFormats.DATE_DMY_NO_HYPHEN
    )} - ${moment(filterDates.end).format(DateFormats.DATE_DMY_NO_HYPHEN)}` +
    (!!period ? ")" : "");

  return (
    <ErrorFallbackUI>
      <div>
        <PageFilter siteFilter dateFilter routesFilter />
      </div>

      <span className={styles.divider} />

      {!!customerId ? (
        <>
          <KeyStatistics
            period={periodRange}
            customerId={customerId}
            siteId={siteId}
            routes={filterRoutes}
            {...filterDates}
          />
          <ChartTestResults
            period={periodRange}
            customerId={customerId}
            siteId={siteId}
            routes={filterRoutes}
            {...filterDates}
          />
          <div className={styles.split}>
            <ChartSubstances
              period={periodRange}
              customerId={customerId}
              siteId={siteId}
              routes={filterRoutes}
              {...filterDates}
            />
            <DashboardMap
              period={periodRange}
              customerId={customerId}
              siteId={siteId}
              routes={filterRoutes}
              {...filterDates}
            />
          </div>

          <Permission
            requiredPermissions={[
              PermissionEnum.ACCOUNTS_CUSTOMER_READ,
              PermissionEnum.ACCOUNTS_SITE_READ,
            ]}
          >
            <ChartNumberOfTests
              period={periodRange}
              customerId={customerId}
              userType={userType}
              {...filterDates}
            />
            <ChartFalsePositive
              period={periodRange}
              customerId={customerId}
              userType={userType}
              {...filterDates}
            />
          </Permission>

          <TableUsers
            period={periodRange}
            customerId={customerId}
            siteId={siteId}
            routes={filterRoutes}
            {...filterDates}
          />
        </>
      ) : (
        <Empty description={t("dashboard.emptyDescription")} />
      )}
    </ErrorFallbackUI>
  );
};

export default memo(DashboardPremium);
